import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/components/pagination";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, CheckCircle2, Plus, Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { CollapsibleSection } from "@/components/collapsible-section";
import { SalonHealthSection } from "@/components/salon-health-section";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

export const Route = createFileRoute("/_authenticated/admin/salons/$id")({
  component: AdminSalonUsersPage,
  // Set by the links on the salon health list, so "Powrót" leads back there.
  validateSearch: z.object({ from: z.enum(["health"]).optional() }),
});

const addOwnerSchema = z.object({
  email: z.string().email("Nieprawidłowy email"),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

type AddOwnerForm = z.infer<typeof addOwnerSchema>;

const partnerMerchantSchema = z.object({
  email: z.string().email("Niepoprawny email"),
  taxId: z.string().min(1, "NIP jest wymagany"),
  regon: z.string().optional(),
  krs: z.string().optional(),
  legalForm: z.coerce.number().int().positive("Wybierz formę prawną"),
  categoryId: z.coerce.number().int().positive("Wybierz kategorię"),
  addressName: z.string().min(1, "Nazwa adresu jest wymagana"),
  street: z.string().min(1, "Ulica jest wymagana"),
  houseNumber: z.string().min(1, "Nr domu jest wymagany"),
  flatNumber: z.string().optional(),
  postalCode: z.string().min(1, "Kod pocztowy jest wymagany"),
  city: z.string().min(1, "Miasto jest wymagane"),
  country: z.string().length(2, "Dwuliterowy kod kraju (np. PL)"),
  contactName: z.string().min(1, "Imię jest wymagane"),
  contactSurname: z.string().min(1, "Nazwisko jest wymagane"),
  contactEmail: z.string().email("Niepoprawny email"),
  contactPhone: z.string().min(1, "Telefon jest wymagany"),
});

type PartnerMerchantForm = z.infer<typeof partnerMerchantSchema>;

const PROVIDER_LABEL = { tpay: "Tpay", stripe: "Stripe" } as const;

/**
 * The salon's payment provider and its Stripe Connect account. The platform
 * only creates the account; the owner finishes Stripe's own onboarding from
 * the link, and the salon can take online payments once Stripe says so.
 */
function PaymentProviderSection({
  salon,
}: {
  salon: {
    id: string;
    paymentProvider: "tpay" | "stripe";
    stripeAccountId: string | null;
    stripeChargesEnabled: boolean;
    hasTpayCredentials: boolean;
  };
}) {
  const utils = trpc.useUtils();
  const refresh = () => utils.admin.getSalon.invalidate({ salonId: salon.id });

  const status = trpc.admin.getStripeOnboardingStatus.useQuery(
    { salonId: salon.id },
    { enabled: !!salon.stripeAccountId, retry: false },
  );
  const provision = trpc.admin.provisionStripeAccount.useMutation({
    onSuccess: (res) => {
      toast.success("Konto Stripe utworzone");
      window.open(res.onboardingUrl, "_blank", "noopener");
      refresh();
    },
    onError: (err) => toast.error(err.message),
  });
  const [existingAccountId, setExistingAccountId] = useState("");
  const linkExisting = trpc.admin.linkExistingStripeAccount.useMutation({
    onSuccess: () => {
      toast.success("Konto Stripe podpięte");
      setExistingAccountId("");
      refresh();
    },
    onError: (err) => toast.error(err.message),
  });
  const switchProvider = trpc.admin.setSalonPaymentProvider.useMutation({
    onSuccess: (res) => {
      toast.success(`Operator płatności: ${PROVIDER_LABEL[res.paymentProvider]}`);
      refresh();
    },
    onError: (err) => toast.error(err.message),
  });

  const copyLink = async (url: string) => {
    await navigator.clipboard.writeText(url);
    toast.success("Link skopiowany");
  };

  const onStripe = salon.paymentProvider === "stripe";
  const stripeReady =
    !!salon.stripeAccountId &&
    (status.data?.chargesEnabled ?? salon.stripeChargesEnabled);
  const warn = onStripe && !stripeReady;

  return (
    <Card
      className={
        warn
          ? "border-2 border-amber-500 bg-amber-50 p-6 dark:bg-amber-950/30"
          : "p-6"
      }
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Operator płatności online</h2>
          <p className="text-sm text-muted-foreground">
            Salon rozlicza płatności w aplikacji przez{" "}
            <strong>{PROVIDER_LABEL[salon.paymentProvider]}</strong>.
            {onStripe && " Subskrypcje rozliczane są zapisaną kartą (bez BLIK)."}
          </p>
        </div>
        <Select
          value={salon.paymentProvider}
          onValueChange={(v) =>
            switchProvider.mutate({
              salonId: salon.id,
              paymentProvider: v as "tpay" | "stripe",
            })
          }
          disabled={switchProvider.isPending}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tpay" disabled={!salon.hasTpayCredentials}>
              Tpay{!salon.hasTpayCredentials && " (brak kluczy)"}
            </SelectItem>
            <SelectItem value="stripe" disabled={!salon.stripeAccountId}>
              Stripe{!salon.stripeAccountId && " (brak konta)"}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {salon.stripeAccountId ? (
        <div className="space-y-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground">Konto Stripe:</span>
            <code className="rounded bg-muted px-1.5 py-0.5">
              {salon.stripeAccountId}
            </code>
            {status.data?.chargesEnabled ? (
              <Badge className="bg-green-600">przyjmuje płatności</Badge>
            ) : status.data?.detailsSubmitted ? (
              <Badge variant="secondary">weryfikacja w toku</Badge>
            ) : (
              <Badge variant="destructive">onboarding nieukończony</Badge>
            )}
          </div>
          {status.data && !status.data.chargesEnabled && (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-muted-foreground">
                Właściciel salonu musi dokończyć weryfikację u Stripe. Link
                jest jednorazowy; każde odświeżenie tej strony wystawia nowy.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyLink(status.data!.onboardingUrl)}
              >
                Kopiuj link do weryfikacji
              </Button>
              <Button size="sm" variant="ghost" onClick={() => status.refetch()}>
                Sprawdź ponownie
              </Button>
            </div>
          )}
          {status.error && (
            <p className="text-destructive">{status.error.message}</p>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <p className="text-muted-foreground">
            Salon nie ma jeszcze konta Stripe. Tworzymy je w jego imieniu (bez
            prowizji platformy, opłaty Stripe ponosi salon), a właściciel
            dokańcza weryfikację u Stripe.
          </p>
          <Button
            size="sm"
            onClick={() => provision.mutate({ salonId: salon.id })}
            disabled={provision.isPending}
          >
            {provision.isPending ? "Tworzenie…" : "Utwórz konto Stripe"}
          </Button>
          <form
            className="flex w-full flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              linkExisting.mutate({
                salonId: salon.id,
                accountId: existingAccountId.trim(),
              });
            }}
          >
            <Label htmlFor="existingStripeAccount" className="text-muted-foreground">
              albo podepnij istniejące konto:
            </Label>
            <Input
              id="existingStripeAccount"
              className="w-64 font-mono"
              placeholder="acct_…"
              value={existingAccountId}
              onChange={(e) => setExistingAccountId(e.target.value)}
            />
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={!existingAccountId.trim() || linkExisting.isPending}
            >
              {linkExisting.isPending ? "Sprawdzanie…" : "Podepnij konto"}
            </Button>
            <p className="w-full text-xs text-muted-foreground">
              Konto musi być podłączone do naszej platformy w Stripe Connect —
              samo ID z obcego konta Stripe zostanie odrzucone.
            </p>
          </form>
        </div>
      )}
    </Card>
  );
}

function TpayPartnerSection({
  salonId,
  hasAccount,
}: {
  salonId: string;
  hasAccount: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [activationLink, setActivationLink] = useState<string | null>(null);
  const utils = trpc.useUtils();

  const { data: legalForms, isLoading: legalFormsLoading } =
    trpc.admin.getTpayLegalForms.useQuery(undefined, { enabled: open });
  const { data: categories, isLoading: categoriesLoading } =
    trpc.admin.getTpayCategories.useQuery(undefined, { enabled: open });

  const provision = trpc.admin.provisionTpayMerchant.useMutation({
    onSuccess: (res) => {
      setActivationLink(res.activationLink);
      utils.admin.getSalon.invalidate({ salonId });
      toast.success("Konto Tpay utworzone");
    },
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<PartnerMerchantForm>({
    resolver: zodResolver(partnerMerchantSchema),
    mode: "onTouched",
    defaultValues: {
      email: "",
      taxId: "",
      regon: "",
      krs: "",
      legalForm: 0,
      categoryId: 0,
      addressName: "",
      street: "",
      houseNumber: "",
      flatNumber: "",
      postalCode: "",
      city: "",
      country: "PL",
      contactName: "",
      contactSurname: "",
      contactEmail: "",
      contactPhone: "",
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    provision.mutate({
      salonId,
      email: values.email,
      taxId: values.taxId,
      regon: values.regon || null,
      krs: values.krs || null,
      legalForm: values.legalForm,
      categoryId: values.categoryId,
      address: [
        {
          name: values.addressName,
          street: values.street,
          houseNumber: values.houseNumber,
          flatNumber: values.flatNumber || undefined,
          postalCode: values.postalCode,
          city: values.city,
          country: values.country.toUpperCase(),
        },
      ],
      contactPerson: [
        {
          name: values.contactName,
          surname: values.contactSurname,
          email: values.contactEmail,
          phone: values.contactPhone,
        },
      ],
    });
  });

  if (hasAccount) {
    return (
      <Card className="border-green-600/40 bg-green-50/50 p-6 dark:bg-green-950/20">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-green-600" />
          <div>
            <h2 className="mb-1 text-lg font-semibold">
              Konto Tpay (partner)
            </h2>
            <p className="text-sm text-muted-foreground">
              Salon ma już utworzone konto Tpay przez program partnerski.
              Klucze API zostały wpięte automatycznie.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-red-500 bg-red-50 p-6 dark:bg-red-950/30">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-red-600" />
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-red-700 dark:text-red-400">
                Konto Tpay (partner)
              </h2>
              <Badge variant="destructive" className="font-semibold">
                Brak konta
              </Badge>
            </div>
            <p className="text-sm text-red-800/80 dark:text-red-300/80">
              Ten salon nie ma jeszcze konta Tpay w programie partnerskim —
              płatności online nie będą działać. Utwórz konto, a klucze API
              zostaną automatycznie wpisane po sukcesie.
            </p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="destructive" className="shrink-0">
              Utwórz konto
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nowe konto Tpay</DialogTitle>
            </DialogHeader>
            <DialogForm onSubmit={onSubmit}>
              <DialogBody className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label>
                      Email firmy <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      aria-invalid={!!form.formState.errors.email}
                      {...form.register("email")}
                    />
                    {form.formState.errors.email && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.email.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label>
                      NIP <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      aria-invalid={!!form.formState.errors.taxId}
                      {...form.register("taxId")}
                    />
                    {form.formState.errors.taxId && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.taxId.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label>REGON</Label>
                    <Input {...form.register("regon")} />
                  </div>
                  <div className="space-y-1">
                    <Label>KRS</Label>
                    <Input {...form.register("krs")} />
                  </div>
                  <div className="space-y-1">
                    <Label>
                      Forma prawna <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={String(form.watch("legalForm") || "")}
                      onValueChange={(v) =>
                        form.setValue("legalForm", Number(v), {
                          shouldValidate: true,
                          shouldTouch: true,
                        })
                      }
                      disabled={legalFormsLoading}
                    >
                      <SelectTrigger
                        className="w-full"
                        aria-invalid={!!form.formState.errors.legalForm}
                      >
                        <SelectValue
                          placeholder={legalFormsLoading ? "Ładowanie…" : "Wybierz"}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {legalForms?.map((it) => (
                          <SelectItem key={it.id} value={String(it.id)}>
                            {it.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.formState.errors.legalForm && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.legalForm.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label>
                      Kategoria <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={String(form.watch("categoryId") || "")}
                      onValueChange={(v) =>
                        form.setValue("categoryId", Number(v), {
                          shouldValidate: true,
                          shouldTouch: true,
                        })
                      }
                      disabled={categoriesLoading}
                    >
                      <SelectTrigger
                        className="w-full"
                        aria-invalid={!!form.formState.errors.categoryId}
                      >
                        <SelectValue
                          placeholder={categoriesLoading ? "Ładowanie…" : "Wybierz"}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {categories?.map((it) => (
                          <SelectItem key={it.id} value={String(it.id)}>
                            {it.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.formState.errors.categoryId && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.categoryId.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Adres</h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1 md:col-span-2">
                      <Label>
                        Nazwa (etykieta){" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        aria-invalid={!!form.formState.errors.addressName}
                        {...form.register("addressName")}
                      />
                      {form.formState.errors.addressName && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.addressName.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label>
                        Ulica <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        aria-invalid={!!form.formState.errors.street}
                        {...form.register("street")}
                      />
                      {form.formState.errors.street && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.street.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label>
                        Nr domu <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        aria-invalid={!!form.formState.errors.houseNumber}
                        {...form.register("houseNumber")}
                      />
                      {form.formState.errors.houseNumber && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.houseNumber.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label>Nr lokalu</Label>
                      <Input {...form.register("flatNumber")} />
                    </div>
                    <div className="space-y-1">
                      <Label>
                        Kod pocztowy <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        aria-invalid={!!form.formState.errors.postalCode}
                        {...form.register("postalCode")}
                      />
                      {form.formState.errors.postalCode && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.postalCode.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label>
                        Miasto <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        aria-invalid={!!form.formState.errors.city}
                        {...form.register("city")}
                      />
                      {form.formState.errors.city && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.city.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label>
                        Kraj (PL) <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        aria-invalid={!!form.formState.errors.country}
                        {...form.register("country")}
                      />
                      {form.formState.errors.country && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.country.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Osoba kontaktowa</h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label>
                        Imię <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        aria-invalid={!!form.formState.errors.contactName}
                        {...form.register("contactName")}
                      />
                      {form.formState.errors.contactName && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.contactName.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label>
                        Nazwisko <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        aria-invalid={!!form.formState.errors.contactSurname}
                        {...form.register("contactSurname")}
                      />
                      {form.formState.errors.contactSurname && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.contactSurname.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label>
                        Email <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        aria-invalid={!!form.formState.errors.contactEmail}
                        {...form.register("contactEmail")}
                      />
                      {form.formState.errors.contactEmail && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.contactEmail.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label>
                        Telefon <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        aria-invalid={!!form.formState.errors.contactPhone}
                        {...form.register("contactPhone")}
                      />
                      {form.formState.errors.contactPhone && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.contactPhone.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {activationLink && (
                  <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
                    <p className="font-medium">Link aktywacyjny</p>
                    <p className="mb-2 text-muted-foreground">
                      Wyślij poniższy link do salonu aby aktywował konto.
                    </p>
                    <a
                      href={activationLink}
                      target="_blank"
                      rel="noreferrer"
                      className="break-all text-blue-700 underline"
                    >
                      {activationLink}
                    </a>
                  </div>
                )}
              </DialogBody>
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setOpen(false)}
                  disabled={provision.isPending}
                >
                  Zamknij
                </Button>
                <Button type="submit" disabled={provision.isPending}>
                  {provision.isPending ? "Tworzenie…" : "Utwórz konto Tpay"}
                </Button>
              </DialogFooter>
            </DialogForm>
          </DialogContent>
        </Dialog>
      </div>
    </Card>
  );
}

function AdminSalonUsersPage() {
  const { id } = Route.useParams();
  const { from } = Route.useSearch();
  // Salon health is a platform view; admin-org members do not get it.
  const { data: adminMe } = trpc.admin.getMe.useQuery();
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const utils = trpc.useUtils();

  const { data: salon, isLoading: salonLoading } =
    trpc.admin.getSalon.useQuery({ salonId: id });

  const { data: usersData, isLoading: usersLoading } =
    trpc.admin.listSalonUsers.useQuery({ salonId: id, page, limit: 20 });

  const addOwnerMutation = trpc.admin.addSalonOwner.useMutation({
    onSuccess: () => {
      toast.success("Właściciel dodany");
      utils.admin.listSalonUsers.invalidate({ salonId: id });
      setDialogOpen(false);
      form.reset();
    },
    onError: (err) => toast.error(err.message),
  });

  const removeUserMutation = trpc.admin.removeSalonUser.useMutation({
    onSuccess: () => {
      toast.success("Użytkownik usunięty");
      utils.admin.listSalonUsers.invalidate({ salonId: id });
    },
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<AddOwnerForm>({
    resolver: zodResolver(addOwnerSchema),
    defaultValues: { email: "", firstName: "", lastName: "" },
  });

  const onSubmit = (data: AddOwnerForm) => {
    addOwnerMutation.mutate({
      salonId: id,
      email: data.email,
      firstName: data.firstName || null,
      lastName: data.lastName || null,
    });
  };

  const totalPages = Math.ceil((usersData?.total ?? 0) / 20);

  if (salonLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  if (!salon) {
    return <p className="text-sm text-destructive">Salon nie znaleziony</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to={from === "health" ? "/admin/health" : "/admin/salons"}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-1 size-4" />
            Powrót
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">{salon.name}</h1>
          <p className="text-sm text-muted-foreground">Szczegóły salonu</p>
        </div>
      </div>

      <TpayPartnerSection salonId={id} hasAccount={!!salon.tpayAccountId} />

      <PaymentProviderSection salon={salon} />

      {adminMe?.isSuperAdmin && <SalonHealthSection salonId={id} />}

      <CollapsibleSection
        id="salon-users"
        title={`Użytkownicy (${usersData?.total ?? 0})`}
        action={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="mr-2 size-4" />
                Dodaj właściciela
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Dodaj właściciela salonu</DialogTitle>
              </DialogHeader>
              <DialogForm onSubmit={form.handleSubmit(onSubmit)}>
                <DialogBody className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="owner-email">Email</Label>
                    <Input
                      id="owner-email"
                      type="email"
                      {...form.register("email")}
                      placeholder="user@example.com"
                    />
                    {form.formState.errors.email && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.email.message}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="owner-first">Imię</Label>
                      <Input
                        id="owner-first"
                        {...form.register("firstName")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="owner-last">Nazwisko</Label>
                      <Input
                        id="owner-last"
                        {...form.register("lastName")}
                      />
                    </div>
                  </div>
                </DialogBody>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setDialogOpen(false)}
                  >
                    Anuluj
                  </Button>
                  <Button
                    type="submit"
                    disabled={addOwnerMutation.isPending}
                  >
                    {addOwnerMutation.isPending ? "Dodawanie..." : "Dodaj"}
                  </Button>
                </DialogFooter>
              </DialogForm>
            </DialogContent>
          </Dialog>
        }
      >
        <Card>
          {usersLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Ładowanie...</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="px-4 py-3 font-medium">Użytkownik</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Rola</th>
                      <th
                        className="px-4 py-3 font-medium"
                        title="Ostatnie użycie aplikacji mobilnej jako klient"
                      >
                        Aktywność w aplikacji
                      </th>
                      <th
                        className="px-4 py-3 font-medium"
                        title="Ostatnia praca w panelu salonu (właściciele i operatorzy)"
                      >
                        Aktywność w panelu
                      </th>
                      <th className="px-4 py-3 font-medium">Dołączył</th>
                      <th className="px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersData?.items.map((user) => (
                      <tr key={user.id} className="border-b last:border-0">
                        <td className="px-4 py-3 font-medium">
                          {user.firstName || user.lastName
                            ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {user.email}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              user.role === "owner" ? "default" : "secondary"
                            }
                          >
                            {user.role === "owner" ? "Właściciel" : "Członek"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {user.lastActiveAt
                            ? new Date(user.lastActiveAt).toLocaleDateString(
                                "pl-PL",
                              )
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {user.lastPanelActiveAt
                            ? new Date(
                                user.lastPanelActiveAt,
                              ).toLocaleDateString("pl-PL")
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {new Date(user.joinedAt).toLocaleDateString("pl-PL")}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (
                                confirm(
                                  `Usunąć ${user.email} z salonu?`,
                                )
                              ) {
                                removeUserMutation.mutate({
                                  salonId: id,
                                  userId: user.id,
                                });
                              }
                            }}
                          >
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {usersData?.items.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-8 text-center text-muted-foreground"
                        >
                          Brak użytkowników
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-4 pb-3">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            </>
          )}
        </Card>
      </CollapsibleSection>
    </div>
  );
}
